import { useState, useEffect, useRef, useLayoutEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { cn } from "@/lib/utils";
import {
  BookOpen,
  Users,
  CreditCard,
  LayoutDashboard,
  Settings,
  BookIcon,
  Moon,
  Sun,
  Monitor,
  Globe,
  UserPen,
  Shield,
  UserCog,
  KeyRound,
  Wrench,
  FileImage,
  BookMarked,
  LayoutPanelTop,
  Tag,
  PercentSquare,
  Trophy,
  Wallet,
  DollarSign,
  Gift,
  TrendingUp,
  HandHeart,
  Upload,
  Target,
  Bell,
  BellRing,
  MessageSquare,
  Zap,
  Trash2,
  FlaskConical,
  Send,
  Bot,
  FileText,
  Database,
  ThumbsUp,
  Smartphone,
  Activity,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { motion } from "framer-motion";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

type SidebarItem = {
  title: string;
  icon: React.ReactNode;
  path: string;
  adminOnly?: boolean;
  superAdminOnly?: boolean;
};

type SidebarSection = {
  title: string;
  items: SidebarItem[];
};

type SidebarProps = {
  collapsed?: boolean;
};

const sidebarSections: SidebarSection[] = [
  {
    title: "Dashboard & Overview",
    items: [
      {
        title: "Dashboard",
        icon: <LayoutDashboard className="w-5 h-5" />,
        path: "/admin/dashboard",
      },
    ],
  },
  {
    title: "Analytics",
    items: [
      {
        title: "Content Analytics",
        icon: <BookOpen className="w-5 h-5" />,
        path: "/admin/analytics/content",
      },
      {
        title: "User Analytics",
        icon: <Users className="w-5 h-5" />,
        path: "/admin/analytics/users",
        adminOnly: true,
      },
      {
        title: "Business Analytics",
        icon: <TrendingUp className="w-5 h-5" />,
        path: "/admin/analytics/business",
        adminOnly: true,
      },
      // {
      //   title: "System Analytics",
      //   icon: <Settings className="w-5 h-5" />,
      //   path: "/admin/analytics/system",
      //   adminOnly: true,
      // },
    ],
  },
  {
    title: "Content Management",
    items: [
      {
        title: "Data Import",
        icon: <Upload className="w-5 h-5" />,
        path: "/admin/data-import",
        adminOnly: true,
      },
      {
        title: "Books",
        icon: <BookOpen className="w-5 h-5" />,
        path: "/admin/books",
      },
      {
        title: "Collections",
        icon: <BookMarked className="w-5 h-5" />,
        path: "/admin/collections",
      },
      {
        title: "Sections",
        icon: <LayoutPanelTop className="w-5 h-5" />,
        path: "/admin/sections",
      },
      {
        title: "Categories",
        icon: <BookIcon className="w-5 h-5" />,
        path: "/admin/categories",
      },
      {
        title: "People",
        icon: <UserPen className="w-5 h-5" />,
        path: "/admin/people",
      },
      {
        title: "Media Library",
        icon: <FileImage className="w-5 h-5" />,
        path: "/admin/media",
      },
    ],
  },
  {
    title: "Users & Subscriptions",
    items: [
      {
        title: "Users",
        icon: <Users className="w-5 h-5" />,
        path: "/admin/users",
      },
      {
        title: "Beta Testers",
        icon: <FlaskConical className="w-5 h-5" />,
        path: "/admin/beta-testers",
        adminOnly: true,
      },
      {
        title: "User Groups",
        icon: <Target className="w-5 h-5" />,
        path: "/admin/user-groups",
        adminOnly: true,
      },
      {
        title: "User Subscriptions",
        icon: <CreditCard className="w-5 h-5" />,
        path: "/admin/user-subscriptions",
        adminOnly: true,
      },
      {
        title: "Account Deletion Feedback",
        icon: <Trash2 className="w-5 h-5" />,
        path: "/admin/account-deletion-feedback",
        adminOnly: true,
      },
      {
        title: "User Feedback",
        icon: <MessageSquare className="w-5 h-5" />,
        path: "/admin/user-feedback",
        adminOnly: true,
      },
      {
        title: "User Book Feedback",
        icon: <MessageSquare className="w-5 h-5" />,
        path: "/admin/user-book-feedback",
        adminOnly: true,
      },
    ],
  },
  {
    title: "Notifications & Campaigns",
    items: [
      {
        title: "Notification Dashboard",
        icon: <Bell className="w-5 h-5" />,
        path: "/admin/notifications",
        adminOnly: true,
      },
      {
        title: "Send Notifications",
        icon: <Send className="w-5 h-5" />,
        path: "/admin/notifications/send",
        adminOnly: true,
      },
      {
        title: "Notification Templates",
        icon: <MessageSquare className="w-5 h-5" />,
        path: "/admin/notifications/templates",
        adminOnly: true,
      },
      {
        title: "Automation Rules",
        icon: <Zap className="w-5 h-5" />,
        path: "/admin/notifications/automation",
        adminOnly: true,
      },
      {
        title: "Campaign Management",
        icon: <BellRing className="w-5 h-5" />,
        path: "/admin/notifications/campaigns",
        adminOnly: true,
      },
      {
        title: "Delivery Analytics",
        icon: <BellRing className="w-5 h-5" />,
        path: "/admin/notifications/analytics",
        adminOnly: true,
      },
      {
        title: "Cleanup",
        icon: <Trash2 className="w-5 h-5" />,
        path: "/admin/notifications/cleanup",
        adminOnly: true,
      },
    ],
  },
  {
    title: "Monetization",
    items: [
      {
        title: "Subscription Plans",
        icon: <Wallet className="w-5 h-5" />,
        path: "/admin/subscription-plans",
      },
      {
        title: "Promotions",
        icon: <PercentSquare className="w-5 h-5" />,
        path: "/admin/promotions",
      },
      {
        title: "Reading Rewards",
        icon: <Trophy className="w-5 h-5" />,
        path: "/admin/reading-rewards",
      },
    ],
  },
  {
    title: "Collaboration",
    items: [
      {
        title: "Collaborators",
        icon: <HandHeart className="w-5 h-5" />,
        path: "/admin/collaborators",
        adminOnly: true,
      },
      {
        title: "Collaborator Earnings",
        icon: <DollarSign className="w-5 h-5" />,
        path: "/admin/collaborators/earnings",
        adminOnly: true,
      },
      {
        title: "Collaborator Payouts",
        icon: <CreditCard className="w-5 h-5" />,
        path: "/admin/collaborators/payouts",
        adminOnly: true,
      },
    ],
  },
  {
    title: "Referral Programme",
    items: [
      {
        title: "Referral Rewards",
        icon: <Gift className="w-5 h-5" />,
        path: "/admin/referrals/rewards",
        adminOnly: true,
      },
      {
        title: "Referral Stats",
        icon: <TrendingUp className="w-5 h-5" />,
        path: "/admin/referrals/stats",
        adminOnly: true,
      },
    ],
  },
  {
    title: "AI Suite",
    items: [
      {
        title: "AI Health",
        icon: <Activity className="w-5 h-5" />,
        path: "/admin/ai/health",
        adminOnly: true,
      },
      {
        title: "Knowledge Base",
        icon: <Database className="w-5 h-5" />,
        path: "/admin/ai/knowledge-base",
        adminOnly: true,
      },
      {
        title: "AI Personas",
        icon: <Bot className="w-5 h-5" />,
        path: "/admin/ai/personas",
        adminOnly: true,
      },
      {
        title: "Prompt Templates",
        icon: <FileText className="w-5 h-5" />,
        path: "/admin/ai/prompt-templates",
        adminOnly: true,
      },
      {
        title: "Ask AI Sessions",
        icon: <MessageSquare className="w-5 h-5" />,
        path: "/admin/ask-ai",
        adminOnly: true,
      },
      {
        title: "Chat Playground",
        icon: <FlaskConical className="w-5 h-5" />,
        path: "/admin/ask-ai/playground",
        adminOnly: true,
      },
      {
        title: "Feedback Analytics",
        icon: <ThumbsUp className="w-5 h-5" />,
        path: "/admin/ai/feedback",
        adminOnly: true,
      },
      {
        title: "User AI Profiles",
        icon: <UserCog className="w-5 h-5" />,
        path: "/admin/ai/user-profiles",
        adminOnly: true,
      },
    ],
  },
  {
    title: "Mobile App",
    items: [
      {
        title: "Onboarding",
        icon: <Smartphone className="w-5 h-5" />,
        path: "/admin/onboarding",
        adminOnly: true,
      },
    ],
  },
  {
    title: "System Settings",
    items: [
      {
        title: "User Roles",
        icon: <UserCog className="w-5 h-5" />,
        path: "/admin/system/user-roles",
        adminOnly: true,
      },
      {
        title: "People Roles",
        icon: <Users className="w-5 h-5" />,
        path: "/admin/system/people-roles",
        adminOnly: true,
      },
      // {
      //   title: "Permissions",
      //   icon: <Shield className="w-5 h-5" />,
      //   path: "/admin/system/permissions",
      //   adminOnly: true,
      // },
      {
        title: "Languages",
        icon: <Globe className="w-5 h-5" />,
        path: "/admin/system/languages",
        adminOnly: true,
      },
      {
        title: "Currencies",
        icon: <DollarSign className="w-5 h-5" />,
        path: "/admin/system/currencies",
        adminOnly: true,
      },
      {
        title: "Payment Methods",
        icon: <CreditCard className="w-5 h-5" />,
        path: "/admin/system/payment-methods",
        adminOnly: true,
      },
      {
        title: "Settings",
        icon: <Wrench className="w-5 h-5" />,
        path: "/admin/system/settings",
        adminOnly: true,
        superAdminOnly: true,
      },
    ],
  },
];

type Theme = "light" | "dark" | "system";

const Sidebar = ({ collapsed = false }: SidebarProps) => {
  const [theme, setTheme] = useState<Theme>("system");
  const location = useLocation();
  const navRef = useRef<HTMLElement>(null);
  const scrollPositionRef = useRef<number>(0);
  const locationRef = useRef(location.pathname);

  // Save scroll position continuously
  const saveScrollPosition = () => {
    if (navRef.current) {
      const scrollTop = navRef.current.scrollTop;
      scrollPositionRef.current = scrollTop;
      sessionStorage.setItem("sidebar-scroll-position", scrollTop.toString());
    }
  };

  // Restore scroll position with multiple attempts
  const restoreScrollPosition = () => {
    const savedPosition = sessionStorage.getItem("sidebar-scroll-position");
    const targetPosition = savedPosition
      ? parseInt(savedPosition, 10)
      : scrollPositionRef.current;

    if (navRef.current && targetPosition > 0) {
      const restore = () => {
        if (navRef.current) {
          navRef.current.scrollTop = targetPosition;
        }
      };

      // Multiple restoration attempts to ensure it works
      restore(); // Immediate
      setTimeout(restore, 0); // Next tick
      setTimeout(restore, 16); // Next frame
      setTimeout(restore, 50); // Delayed
      requestAnimationFrame(restore); // Next animation frame
    }
  };

  // Use useLayoutEffect for synchronous restoration before paint
  useLayoutEffect(() => {
    if (locationRef.current !== location.pathname) {
      locationRef.current = location.pathname;
      restoreScrollPosition();
    }
  }, [location.pathname]);

  // Also restore on mount
  useEffect(() => {
    restoreScrollPosition();
  }, []);

  // Initialize theme based on system or localStorage
  useEffect(() => {
    const storedTheme = localStorage.getItem("ui-theme") as Theme | null;
    if (storedTheme) {
      setTheme(storedTheme);
      applyTheme(storedTheme);
    } else {
      // Default to system
      setTheme("system");
      applySystemTheme();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Apply system theme based on media query
  const applySystemTheme = () => {
    const isDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
    document.documentElement.classList.toggle("dark", isDark);
  };

  // Apply specified theme
  const applyTheme = (newTheme: Theme) => {
    if (newTheme === "system") {
      applySystemTheme();
    } else {
      document.documentElement.classList.toggle("dark", newTheme === "dark");
    }
    localStorage.setItem("ui-theme", newTheme);
  };

  // Set and apply new theme
  const changeTheme = (newTheme: Theme) => {
    setTheme(newTheme);
    applyTheme(newTheme);
  };

  // Add listener for system theme changes
  useEffect(() => {
    if (theme === "system") {
      const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
      const handleChange = () => applySystemTheme();
      mediaQuery.addEventListener("change", handleChange);
      return () => mediaQuery.removeEventListener("change", handleChange);
    }
  }, [theme]);

  // Get current theme icon
  const getThemeIcon = () => {
    switch (theme) {
      case "light":
        return <Sun className="w-5 h-5" />;
      case "dark":
        return <Moon className="w-5 h-5" />;
      case "system":
        return <Monitor className="w-5 h-5" />;
    }
  };

  // Helper function to check if a menu item is active
  const isMenuActive = (path: string) => {
    // Special handling for routes where parent path is a prefix of child paths
    if (path === "/admin/collaborators") {
      return location.pathname === "/admin/collaborators";
    }
    if (path === "/admin/collaborators/earnings") {
      return location.pathname === "/admin/collaborators/earnings";
    }
    if (path === "/admin/collaborators/payouts") {
      return location.pathname === "/admin/collaborators/payouts";
    }
    if (path === "/admin/ask-ai") {
      return location.pathname === "/admin/ask-ai";
    }
    if (path === "/admin/notifications") {
      return location.pathname === "/admin/notifications";
    }

    // Default behavior for other routes
    return (
      location.pathname === path || location.pathname.startsWith(`${path}/`)
    );
  };

  return (
    <motion.aside
      initial={false}
      animate={{ width: collapsed ? 64 : 256 }}
      transition={{ duration: 0.3, ease: "easeInOut" }}
      className={cn(
        "flex flex-col h-screen bg-sidebar text-sidebar-foreground border-r border-sidebar-border relative z-10",
        collapsed ? "items-center" : ""
      )}
    >
      {/* Logo header - always show, but text only when not collapsed */}
      <div className="flex items-center justify-center p-4 border-b border-sidebar-border">
        <div className="flex items-center gap-3">
          <img
            src="/hriatna-eng-192x192.png"
            alt="Hriatna Eng Logo"
            className={cn(
              "w-7 h-7 m-0.5",
              (theme === "dark" ||
                (theme === "system" &&
                  window.matchMedia("(prefers-color-scheme: dark)").matches)) &&
                "brightness-0 invert"
            )}
          />
          {!collapsed && (
            <span className="text-xl font-bold text-sidebar-foreground">
              Hriatna Eng
            </span>
          )}
        </div>
      </div>

      <nav
        ref={navRef}
        className="flex-1 py-4 overflow-y-auto"
        onScroll={saveScrollPosition}
        style={{ scrollBehavior: "auto" }}
      >
        {sidebarSections.map((section) => (
          <div key={section.title} className="mb-6">
            {!collapsed && (
              <h3 className="px-4 mb-2 text-xs font-semibold text-sidebar-muted uppercase tracking-wider">
                {section.title}
              </h3>
            )}
            <ul className="space-y-1 px-2">
              {section.items.map((item) => {
                const active = isMenuActive(item.path);
                return (
                  <li key={item.path} className="relative">
                    {collapsed ? (
                      <TooltipProvider delayDuration={0}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Link
                              to={item.path}
                              className={cn(
                                "flex items-center justify-center p-3 rounded-md transition-all duration-300",
                                active
                                  ? "bg-gradient-to-r from-[#3d6de0] to-[#447AED] text-white backdrop-blur-sm"
                                  : "text-sidebar-foreground hover:bg-sidebar-accent/60 hover:text-sidebar-primary"
                              )}
                            >
                              <div
                                className={cn(
                                  "transition-transform duration-300",
                                  active ? "scale-110" : ""
                                )}
                              >
                                {item.icon}
                              </div>
                            </Link>
                          </TooltipTrigger>
                          <TooltipContent side="right" sideOffset={10}>
                            {item.title}
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    ) : (
                      <Link
                        to={item.path}
                        className={cn(
                          "group flex items-center py-2.5 px-3 rounded-md transition-all duration-300",
                          active
                            ? "bg-gradient-to-r from-[#3d6de0] to-[#447AED] text-white backdrop-blur-sm"
                            : "text-sidebar-foreground hover:bg-sidebar-accent/60 hover:text-sidebar-primary"
                        )}
                      >
                        <div
                          className={cn(
                            "transition-transform duration-300",
                            active ? "scale-110" : "group-hover:scale-105"
                          )}
                        >
                          {item.icon}
                        </div>
                        <span
                          className={cn(
                            "ml-3 transition-all duration-300",
                            active
                              ? "font-medium"
                              : "group-hover:translate-x-0.5"
                          )}
                        >
                          {item.title}
                        </span>
                        {active && (
                          <div className="ml-auto w-1 h-4 bg-white/30 rounded-full" />
                        )}
                      </Link>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </nav>

      <div className="p-4 border-t border-sidebar-border">
        {/* Theme Toggle with Dropdown */}
        {collapsed ? (
          <TooltipProvider delayDuration={0}>
            <Tooltip>
              <TooltipTrigger asChild>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="w-full text-sidebar-foreground hover:bg-sidebar-accent transition-colors"
                    >
                      {getThemeIcon()}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    side="right"
                    align="start"
                    sideOffset={10}
                    className="bg-popover border border-border"
                  >
                    <DropdownMenuItem
                      onClick={() => changeTheme("light")}
                      className="flex items-center cursor-pointer"
                    >
                      <Sun className="w-4 h-4 mr-2" />
                      Light
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => changeTheme("dark")}
                      className="flex items-center cursor-pointer"
                    >
                      <Moon className="w-4 h-4 mr-2" />
                      Dark
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => changeTheme("system")}
                      className="flex items-center cursor-pointer"
                    >
                      <Monitor className="w-4 h-4 mr-2" />
                      System
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </TooltipTrigger>
              <TooltipContent side="right" sideOffset={10}>
                Theme
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ) : (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                className="flex items-center justify-start w-full text-sidebar-foreground hover:bg-sidebar-accent transition-colors"
              >
                {getThemeIcon()}
                <span className="ml-3">
                  {theme === "light"
                    ? "Light"
                    : theme === "dark"
                    ? "Dark"
                    : "System"}
                </span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="bg-popover border border-border">
              <DropdownMenuItem
                onClick={() => changeTheme("light")}
                className="flex items-center cursor-pointer"
              >
                <Sun className="w-4 h-4 mr-2" />
                Light
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => changeTheme("dark")}
                className="flex items-center cursor-pointer"
              >
                <Moon className="w-4 h-4 mr-2" />
                Dark
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => changeTheme("system")}
                className="flex items-center cursor-pointer"
              >
                <Monitor className="w-4 h-4 mr-2" />
                System
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </motion.aside>
  );
};

export default Sidebar;
